'use client';

import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';

const RemoveDocumentModal = ({ isOpen, onClose, document, envelopeSubject, onRemove, isSubmitting }) => {
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [errors, setErrors] = useState({});

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setReason('');
      setConfirmed(false);
      setErrors({});
    }
  }, [isOpen]);

  const validateForm = () => {
    const newErrors = {};
    if (!reason.trim()) {
      newErrors.reason = 'Reason is required';
    } else if (reason.trim().length < 10) {
      newErrors.reason = 'Reason must be at least 10 characters';
    }
    if (!confirmed) {
      newErrors.confirmed = 'You must confirm this action';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onRemove({ reason: reason.trim() });
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-red-700">Remove Document</h2>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4 p-3 bg-gray-50 rounded-md">
              <p className="text-sm text-gray-600">Envelope:</p>
              <p className="font-medium text-gray-900">{envelopeSubject || 'Untitled Document'}</p>
              <div className="mt-2 flex items-center gap-2 text-sm text-gray-700">
                <FileText className="w-4 h-4 text-gray-400" />
                <span className="font-medium">{document?.name}</span>
              </div>
            </div>

            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md flex gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-700">
                <p className="font-medium">This action cannot be undone.</p>
                <p className="mt-1">This document and all signature fields on it will be permanently removed from the envelope. Recipients who only need to sign this document may be left with no action items.</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for removal * <span className="text-gray-400 font-normal">(min 10 characters)</span>
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Explain why this document needs to be removed..."
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-md ${errors.reason ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-red-500`}
                  disabled={isSubmitting}
                />
                {errors.reason && <p className="text-red-500 text-sm mt-1">{errors.reason}</p>}
              </div>

              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="confirmRemoveDoc"
                  checked={confirmed}
                  onChange={(e) => { setConfirmed(e.target.checked); setErrors(prev => ({ ...prev, confirmed: undefined })); }}
                  className="mr-2 mt-1"
                  disabled={isSubmitting}
                />
                <label htmlFor="confirmRemoveDoc" className={`text-sm ${errors.confirmed ? 'text-red-600' : 'text-gray-700'}`}>
                  I understand this will permanently remove the document from the envelope.
                </label>
              </div>
              {errors.confirmed && <p className="text-red-500 text-sm -mt-2">{errors.confirmed}</p>}

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  onClick={onClose}
                  className="flex-1 bg-gray-100 text-gray-700 hover:bg-gray-200"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-red-600 text-white hover:bg-red-700"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Removing...' : 'Remove Document'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default RemoveDocumentModal;
